import type { AppLocale } from '../i18n';

/** Pick admin-provided bilingual content field for the active UI locale. */
export function pickLocalized(
  english?: string | null,
  persian?: string | null,
  locale: AppLocale = 'en'
): string {
  if (locale === 'fa') {
    const fa = persian?.trim();
    if (fa) return fa;
  }
  const en = english?.trim();
  if (en) return en;
  return persian?.trim() ?? '';
}

export function pickLocalizedTitle(
  item: { title?: string | null; titleFa?: string | null; Title?: string | null; TitleFa?: string | null },
  locale: AppLocale
): string {
  const en = item.title ?? item.Title ?? '';
  const fa = item.titleFa ?? item.TitleFa ?? '';
  return pickLocalized(en, fa, locale);
}

/** True when the admin has populated a Persian title field. */
export function hasFarsiTitle(
  item: { titleFa?: string | null; TitleFa?: string | null } | null | undefined
): boolean {
  if (!item) return false;
  return Boolean((item.titleFa ?? item.TitleFa ?? '').trim());
}

/** True when both English and Persian title fields are populated (admin bilingual content). */
export function hasBilingualTitle(
  item: { title?: string | null; titleFa?: string | null; Title?: string | null; TitleFa?: string | null } | null | undefined
): boolean {
  if (!item) return false;
  const en = (item.title ?? item.Title ?? '').trim();
  const fa = (item.titleFa ?? item.TitleFa ?? '').trim();
  return Boolean(en && fa);
}

type BilingualFieldItem = {
  title?: string | null;
  titleFa?: string | null;
  Title?: string | null;
  TitleFa?: string | null;
  description?: string | null;
  descriptionFa?: string | null;
  Description?: string | null;
  DescriptionFa?: string | null;
};

/** Bilingual when both locales exist on title and/or description. */
export function hasBilingualContentFields(item: BilingualFieldItem | null | undefined): boolean {
  if (!item) return false;
  if (hasBilingualTitle(item)) return true;
  const descEn = (item.description ?? item.Description ?? '').trim();
  const descFa = (item.descriptionFa ?? item.DescriptionFa ?? '').trim();
  return Boolean(descEn && descFa);
}

export function pickLocalizedDescription(
  item: {
    description?: string | null;
    descriptionFa?: string | null;
    Description?: string | null;
    DescriptionFa?: string | null;
  },
  locale: AppLocale
): string {
  const en = item.description ?? item.Description ?? '';
  const fa = item.descriptionFa ?? item.DescriptionFa ?? '';
  return pickLocalized(en, fa, locale);
}

export function pickLibraryBookTitle(
  book: { title?: string | null; farsiTitle?: string | null },
  locale: AppLocale
): string {
  return pickLocalized(book.title, book.farsiTitle, locale);
}

export function pickLibraryBookAuthor(
  book: { author?: string | null; farsiAuthor?: string | null },
  locale: AppLocale
): string {
  return pickLocalized(book.author, book.farsiAuthor, locale);
}

export function pickLocalizedCategoryName(
  item: { name?: string | null; nameFa?: string | null; Name?: string | null; NameFa?: string | null },
  locale: AppLocale
): string {
  const en = item.name ?? item.Name ?? '';
  const fa = item.nameFa ?? item.NameFa ?? '';
  return pickLocalized(en, fa, locale);
}

export function pickLocalizedTagPublishedName(
  item: {
    namePublished?: string | null;
    namePublishedFa?: string | null;
    NamePublished?: string | null;
    NamePublishedFa?: string | null;
  },
  locale: AppLocale
): string {
  const en = item.namePublished ?? item.NamePublished ?? '';
  const fa = item.namePublishedFa ?? item.NamePublishedFa ?? '';
  return pickLocalized(en, fa, locale);
}

export function pickLocalizedTagPublishedDescription(
  item: {
    descriptionPublished?: string | null;
    descriptionPublishedFa?: string | null;
    DescriptionPublished?: string | null;
    DescriptionPublishedFa?: string | null;
  },
  locale: AppLocale
): string {
  const en = item.descriptionPublished ?? item.DescriptionPublished ?? '';
  const fa = item.descriptionPublishedFa ?? item.DescriptionPublishedFa ?? '';
  return pickLocalized(en, fa, locale);
}

export function pickBlockTextContent(
  block: {
    textContent?: string | null;
    textContentFa?: string | null;
    TextContent?: string | null;
    TextContentFa?: string | null;
  },
  locale: AppLocale
): string {
  const en = block.textContent ?? block.TextContent ?? '';
  const fa = block.textContentFa ?? block.TextContentFa ?? '';
  return pickLocalized(en, fa, locale);
}

export function pickBlockTextFormatting(
  block: {
    textFormatting?: string | null;
    textFormattingFa?: string | null;
    TextFormatting?: string | null;
    TextFormattingFa?: string | null;
  },
  locale: AppLocale
): string | undefined {
  const en = block.textFormatting ?? block.TextFormatting ?? undefined;
  const fa = block.textFormattingFa ?? block.TextFormattingFa ?? undefined;
  const picked = pickLocalized(en ?? '', fa ?? '', locale);
  return picked || en || fa || undefined;
}

export function pickBlockTitle(
  block: { title?: string | null; titleFa?: string | null; Title?: string | null; TitleFa?: string | null },
  locale: AppLocale
): string {
  return pickLocalizedTitle(block, locale);
}

export function pickBlockTips(
  block: {
    tips?: string | null;
    tipsFa?: string | null;
    Tips?: string | null;
    TipsFa?: string | null;
  },
  locale: AppLocale
): string | undefined {
  const en = block.tips ?? block.Tips ?? undefined;
  const fa = block.tipsFa ?? block.TipsFa ?? undefined;
  const picked = pickLocalized(en ?? '', fa ?? '', locale);
  return picked || en || fa || undefined;
}

export function pickBlockStickyAudio(block: {
  stickyAudio?: string | null;
  StickyAudio?: string | null;
}): string | undefined {
  return block.stickyAudio ?? block.StickyAudio ?? undefined;
}

type BlockLocaleFields = {
  textContent?: string | null;
  textContentFa?: string | null;
  TextContent?: string | null;
  TextContentFa?: string | null;
  title?: string | null;
  titleFa?: string | null;
  Title?: string | null;
  TitleFa?: string | null;
};

function blockFieldPairPopulated(
  en?: string | null,
  fa?: string | null
): { hasEn: boolean; hasFa: boolean } {
  return { hasEn: Boolean((en ?? '').trim()), hasFa: Boolean((fa ?? '').trim()) };
}

/** Which EN/FA column is shown for a bilingual field pair (UI locale when both exist). */
export function pickLocalizedFieldLocale(
  en?: string | null,
  fa?: string | null,
  uiLocale: AppLocale = 'en'
): AppLocale {
  const pair = blockFieldPairPopulated(en, fa);
  if (pair.hasEn && !pair.hasFa) return 'en';
  if (pair.hasFa && !pair.hasEn) return 'fa';
  return uiLocale;
}

/** Learner lesson blocks: show authored EN/FA; UI locale only when both columns exist. */
export function pickBlockDisplayLocale(block: BlockLocaleFields, uiLocale: AppLocale): AppLocale {
  const text = blockFieldPairPopulated(
    block.textContent ?? block.TextContent,
    block.textContentFa ?? block.TextContentFa
  );
  if (text.hasEn || text.hasFa) {
    return pickLocalizedFieldLocale(
      block.textContent ?? block.TextContent,
      block.textContentFa ?? block.TextContentFa,
      uiLocale
    );
  }
  return pickLocalizedFieldLocale(
    block.title ?? block.Title,
    block.titleFa ?? block.TitleFa,
    uiLocale
  );
}

type BilingualExplanationFields = {
  explanation?: string | null;
  explanationFa?: string | null;
  Explanation?: string | null;
  ExplanationFa?: string | null;
};

export function pickQuestionExplanation(
  item: BilingualExplanationFields,
  locale: AppLocale
): string {
  const en = item.explanation ?? item.Explanation ?? '';
  const fa = item.explanationFa ?? item.ExplanationFa ?? '';
  return pickLocalized(en, fa, locale);
}

/** Block-based or legacy question prompt for learner quiz/exercise UI. */
export function pickQuestionBody(
  item: {
    question?: string | null;
    textContent?: string | null;
    textContentFa?: string | null;
    TextContent?: string | null;
    TextContentFa?: string | null;
    textFormatting?: string | null;
    textFormattingFa?: string | null;
    TextFormatting?: string | null;
    TextFormattingFa?: string | null;
  },
  locale: AppLocale
): { textContent: string; textFormatting?: string } {
  const text = pickBlockTextContent(item, locale);
  const textFormatting = pickBlockTextFormatting(item, locale);
  if (text.trim()) {
    return { textContent: text, textFormatting };
  }
  return { textContent: item.question?.trim() ?? '', textFormatting };
}

export function pickOptionBody(
  item: {
    textContent?: string | null;
    textContentFa?: string | null;
    TextContent?: string | null;
    TextContentFa?: string | null;
    textFormatting?: string | null;
    textFormattingFa?: string | null;
    TextFormatting?: string | null;
    TextFormattingFa?: string | null;
  },
  locale: AppLocale
): { textContent: string; textFormatting?: string } {
  return {
    textContent: pickBlockTextContent(item, locale),
    textFormatting: pickBlockTextFormatting(item, locale),
  };
}

/** Plain text from bilingual option blocks for admin preview (puzzle / fill-in-blank). */
export function getPlainTextFromOptionBlocksForLocale(
  blocks: Array<{ type?: string; textContent?: string | null; textContentFa?: string | null }> | null | undefined,
  locale: AppLocale
): string {
  const textBlock = (blocks ?? []).find((b) => b.type === 'text');
  if (!textBlock) return '';
  return plainTextFromHtml(pickBlockTextContent(textBlock, locale));
}

export function parseStoredFormattingJson(raw?: string | null): Record<string, unknown> | undefined {
  if (!raw?.trim()) return undefined;
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

export function plainTextFromHtml(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]+>/g, '').trim();
  }
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return (temp.textContent || temp.innerText || '').trim();
}

/** Overlay locale-specific question/option/explanation fields for learner display. */
export function localizeQuizExerciseQuestion<T extends Record<string, unknown>>(
  question: T,
  locale: AppLocale
): T {
  const body = pickQuestionBody(question, locale);
  const rawQuestion = typeof question.question === 'string' ? question.question.trim() : '';
  const rawTextContent = typeof question.textContent === 'string' ? question.textContent.trim() : '';
  const rowKind = question.rowKind as number | undefined;
  const isStructuralMarker =
    rowKind === 1 ||
    rowKind === 2 ||
    rawQuestion === '__COMMENT__' ||
    rawQuestion === '__PAGE_SEPARATOR__' ||
    rawTextContent === '__COMMENT__' ||
    rawTextContent === '__PAGE_SEPARATOR__';
  const localizedOptions = Array.isArray(question.questionOptions)
    ? (question.questionOptions as Record<string, unknown>[]).map((opt) => {
        const optBody = pickOptionBody(opt, locale);
        return {
          ...opt,
          textContent: optBody.textContent || opt.textContent,
          textFormatting: optBody.textFormatting ?? opt.textFormatting,
        };
      })
    : question.questionOptions;

  return {
    ...question,
    textContent: body.textContent || question.textContent,
    textFormatting: body.textFormatting ?? question.textFormatting,
    question: isStructuralMarker
      ? rawQuestion
      : body.textContent.trim()
        ? body.textContent
        : question.question,
    explanation: pickQuestionExplanation(question, locale) || question.explanation,
    questionOptions: localizedOptions,
  };
}
